import React from "react";

import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  Paper,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

export interface SplitFormShellProps {
  /** Vertical rail — rendered on sm and up. */
  rail: React.ReactNode;
  /** Compact horizontal navigation — rendered on xs only, in place of the rail. */
  stepper: React.ReactNode;
  form: React.ReactNode;
  preview: React.ReactNode;
  /** Primary page actions; in the rail on lg, in a bar pinned to the shell's foot below it. */
  actions: React.ReactNode;
  previewOpen: boolean;
  onPreviewOpenChange: (open: boolean) => void;
  railWidth?: number;
  previewLabel?: string;
  /**
   * The control that leaves the preview, which reads as where it goes rather than what it closes:
   * "Edit", not "Hide preview". Also its accessible name.
   */
  hidePreviewLabel?: string;
  /**
   * Controls belonging to the preview — view switches and the like. They sit in the preview's
   * header at every size, so they stay put while the preview under them scrolls.
   */
  previewToolbar?: React.ReactNode;
}

/**
 * A form page with a live preview beside it, in three layouts (MUI's unmodified breakpoints):
 *
 * - `lg` ≥1200 — rail | form | preview, side by side, actions in the rail.
 * - `sm`/`md` 600–1199 — rail stays left, the preview becomes a sheet over the form, and the
 *   actions move to a bar along the foot of the shell, which stays over the sheet.
 * - `xs` <600 — the rail becomes the stepper; everything else as above.
 *
 * Every slot is present in every layout; nothing is gated.
 *
 * ## The shell fills its parent, and the columns scroll — the page does not
 *
 * It needs a parent with a **definite height**, and it takes all of it. The columns then scroll
 * inside that box, which is what keeps the preview beside the field being typed into.
 *
 * The first version did this with `position: sticky` and a `100vh`-derived `max-height`, so the
 * page scrolled *and* the preview scrolled: **two vertical scrollbars, side by side at the right
 * edge**, one of which was redundant. Sizing the shell to its container instead leaves the
 * outermost box nothing to scroll, so its bar goes away and the preview's sits where a page bar
 * would. The cost is the definite-height requirement: a host that scrolls the window itself has to
 * wrap this in a box of its own height (`100vh`, or the viewport minus a fixed header).
 */
export const SplitFormShell: React.FC<SplitFormShellProps> = ({
  rail,
  stepper,
  form,
  preview,
  actions,
  previewOpen,
  onPreviewOpenChange,
  railWidth = 208,
  previewLabel = "Preview",
  hidePreviewLabel = "Hide preview",
  previewToolbar,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isWide = useMediaQuery(theme.breakpoints.up("lg"));

  /** The preview's own header, the same at every size bar the way back it only needs in the sheet. */
  const previewHeader = (
    <Box
      sx={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 1,
        rowGap: 0.5,
        minHeight: 40,
        px: 2,
        py: 1,
      }}
    >
      {!isWide && (
        <Button
          size="small"
          startIcon={<ArrowBack />}
          onClick={() => onPreviewOpenChange(false)}
          sx={{ flexShrink: 0, color: "text.secondary" }}
        >
          {hidePreviewLabel}
        </Button>
      )}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: ".8px",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {previewLabel}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {previewToolbar}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        // Fills the parent rather than growing past it: see the note above about the second
        // scrollbar this replaces.
        height: "100%",
        // The frame the action bar and the preview sheet are pinned to — see them below.
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {isPhone && stepper}

      <Box
        sx={{
          flex: "1 1 auto",
          // Without this a flex item refuses to shrink below its content, and the columns would
          // push the row past the viewport instead of scrolling inside it.
          minHeight: 0,
          display: "flex",
          gap: 3,
          width: "100%",
        }}
      >
        {!isPhone && (
          <Box
            sx={{
              pl: { xs: 2, sm: 3 },
              py: 3,
              flex: `0 0 ${railWidth}px`,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
            }}
          >
            {rail}
          </Box>
        )}

        <Box
          component="main"
          sx={{
            // Form and preview share the space left by the rail evenly.
            flex: "1 1 0",
            minWidth: 0,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            // Room for the fixed bottom bar wherever the actions are not in the rail. Inside the
            // scroller, so the last card can be scrolled clear of it rather than sitting under it.
            pb: isWide ? 0 : 12,
          }}
        >
          {form}
        </Box>

        {isWide && (
          <Box
            sx={{
              // Same basis as the form column, so the two are always equal width.
              flex: "1 1 0",
              // Without this the flex item keeps `min-width: auto`, and the preview's intrinsic
              // min-content width overrides the basis and starves the form column.
              minWidth: 0,
              minHeight: 0,
              // The label stays put and the previewed bulletin scrolls under it.
              display: "flex",
              flexDirection: "column",
            }}
          >
            {previewHeader}
            {/* The previewed content has its own comfortable width; let it scroll here rather than
                push the columns around. Padded like the sheet's: a scroller clips at its padding
                box, so without it a previewed card's shadow is cut off along the edges. */}
            <Box sx={{ minHeight: 0, overflow: "auto", p: 2 }}>{preview}</Box>
          </Box>
        )}
      </Box>

      {!isWide && (
        <>
          <Slide direction="up" in={previewOpen} mountOnEnter unmountOnExit>
            <Paper
              square
              elevation={0}
              sx={{
                // Inside the shell, not over the viewport: a modal `Drawer` portals to the body
                // and covers whatever the host has around this page — its top bar and side nav
                // included. The preview belongs to the form, so it covers the form.
                position: "absolute",
                inset: 0,
                // Under the bar below, which stays reachable over the sheet: a preview toggle in
                // the actions has to be able to close what it opened.
                zIndex: 1,
                // Header holds its place; only the preview under it scrolls.
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                {previewHeader}
              </Box>
              {/* Same bottom room as the form column: the bar sits over this, not beside it. */}
              <Box
                sx={{ p: 2, pb: 12, flex: 1, minHeight: 0, overflowY: "auto" }}
              >
                {preview}
              </Box>
            </Paper>
          </Slide>

          <Box
            sx={{
              // Pinned to the shell rather than the viewport, for the same reason as the sheet:
              // `fixed` put the bar's left end under a host side nav, and the controls with it.
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              px: 2,
              py: 1.5,
              backgroundColor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              boxShadow: "0 -2px 8px rgba(0,0,0,.1)",
            }}
          >
            {actions}
          </Box>
        </>
      )}
    </Box>
  );
};
